import { defineStore } from 'pinia';
import {formatDate} from "@/utils/dateUtils";

export const useMealPlanStore = defineStore('mealPlan', {
  state: () => ({
    orders: [],
  }),

  actions: {
    addOrder(shopId, orderType, mealPlanId, reservationDateTime) {
      // Normalize reservationDateTime to ensure consistent comparison
      const normalizedReservationDateTime = new Date(reservationDateTime).toISOString();

      // Check if an existing order matches the given criteria
      let order = this.orders.find(order =>
        order.shopId === shopId &&
        order.orderType === orderType &&
        order.mealPlanId === mealPlanId &&
        order.reservationDateTime === normalizedReservationDateTime
      );

      // If order exists, return it (prevents duplicate orders)
      if (order) {
        return order;
      }

      // Otherwise, create a new order
      const newOrderId = Date.now().toString(); // Unique orderId
      order = {
        orderId: newOrderId,
        shopId,
        orderType,
        mealPlanId: mealPlanId,
        date: formatDate(reservationDateTime),
        reservationDateTime: normalizedReservationDateTime, // Use normalized value
        products: [],
        totalPrice: 0.00,
      };

      this.orders.push(order);
      return order;
    },

    addProductToOrder(orderId, productId, encodedOptions, price, quantity, groupId) {
      const order = this.getOrder(orderId);
      if (!order) {
        console.warn(`Order with ID ${orderId} not found.`);
        return;
      }

      // Find product in the order, considering encodedOptions only if it is defined
      const existingProduct = order.products.find(product =>
        product.id === productId &&
        (encodedOptions !== undefined ? product.encodedOptions === encodedOptions : product.encodedOptions === undefined)
      );

      if (existingProduct) {
        existingProduct.quantity += quantity;
        existingProduct.price = price; // Update price in case it changes
        if (encodedOptions !== undefined) {
          existingProduct.encodedOptions = encodedOptions; // Ensure options are updated if defined
        }
      } else {
        // Add a new product entry, excluding encodedOptions if it is undefined
        const newProduct = { id: productId, price, quantity, groupId };
        if (encodedOptions !== undefined) {
          newProduct.encodedOptions = encodedOptions;
        }
        order.products.push(newProduct);
      }

      // Update total price
      order.totalPrice = order.products.reduce((sum, p) => sum + p.price * p.quantity, 0);
    },

    removeProductFromOrder(orderId, productId, quantityToRemove, encodedOptions) {
      const order = this.getOrder(orderId);
      if (!order) {
        console.warn(`Order with ID ${orderId} not found.`);
        return;
      }

      // Find the product in the order, considering encodedOptions only if it is defined
      const productIndex = order.products.findIndex(product =>
        product.id === productId &&
        (encodedOptions !== undefined ? product.encodedOptions === encodedOptions : product.encodedOptions === undefined)
      );

      if (productIndex === -1) {
        console.warn(`Product with ID ${productId} not found in order.`);
        return;
      }

      const product = order.products[productIndex];
      if (product.quantity > quantityToRemove) {
        product.quantity -= quantityToRemove;
      } else {
        order.products.splice(productIndex, 1);
      }

      // Recalculate total price
      order.totalPrice = order.products.reduce((sum, p) => sum + p.price * p.quantity, 0);

      // If order is empty, remove it
      if (order.products.length === 0) {
        this.orders = this.orders.filter(o => o.orderId !== orderId);
      }
    },

    setPickupNumberForOrder(orderId, pickupNumber) {
      const order = this.getOrder(orderId);
      if (!order) {
        console.warn(`Order with ID ${orderId} not found.`);
        return;
      }
      order.pickupNumber = pickupNumber;
    },

    setStateForOrder(orderId, state) {
      const order = this.getOrder(orderId);
      if (!order) {
        console.warn(`Order with ID ${orderId} not found.`);
        return;
      }
      order.state = state;
    },

    remove(orderId) {
      this.orders = this.orders.filter(o => o.orderId !== orderId);
    },

    reset() {
      this.orders = [];
    },
  },

  getters: {
    getOrder: state => orderId => state.orders.find(o => o.orderId === orderId),
  },

  persist: {
    storage: localStorage,
  },
});
